import { QuickviewDataLoader } from "./quickview-data.js";

class QuickviewTrigger extends HTMLElement {
  constructor() {
    super();
    this._isOpen = false;
    this._loader = new QuickviewDataLoader();
  }

  connectedCallback() {
    this._button = this.querySelector("[data-trigger]");
    this._contentWrapper = this.querySelector("[data-quickview-wrapper]");

    this.addEventListener("mouseenter", () => {
      this._getData();
    });

    this.addEventListener("click", (e) => {
      const variantButton = e.target.closest("[data-variant-id]");
      if (variantButton) {
        this._addToCart(variantButton);
        return;
      }

      const trigger = e.target.closest("[data-trigger]");
      if (!trigger) return;

      if (this._isOpen) {
        this._close();
        return;
      }

      this._getData().then((nodes) => {
        const content = this._content;
        if (content && !content.hasChildNodes()) {
          content.append(nodes);
        }
        this._open();
      });
    });

    this.handleKeyUp = (e) => {
      if (this._isOpen && e.key === "Escape") {
        this._close();
        this._button.focus();
      }
    };
    document.addEventListener("keyup", this.handleKeyUp);

    this.handlePointerDown = (e) => {
      if (this._isOpen && !this.contains(e.target)) {
        this._close();
      }
    };
    document.addEventListener("pointerdown", this.handlePointerDown);

    this.addEventListener("quickview:close", () => {
      if (this._isOpen) this._close();
    });
  }

  disconnectedCallback() {
    document.removeEventListener("keyup", this.handleKeyUp);
    document.removeEventListener("pointerdown", this.handlePointerDown);
  }

  _open() {
    if (this._isOpen) return;
    this._isOpen = true;

    const wrapper = this._contentWrapper;
    this._button.setAttribute("aria-expanded", "true");

    this._removeTransitionListener();
    wrapper.style.display = null;
    wrapper.style.overflow = "hidden";
    wrapper.style.height = "0";

    this._handleTransitionEnd = (e) => {
      if (e.target !== wrapper) return;
      this._removeTransitionListener();
      wrapper.style.height = null;
      wrapper.style.overflow = "visible";
      wrapper.querySelector("button")?.focus();
    };
    wrapper.addEventListener("transitionend", this._handleTransitionEnd);

    window.requestAnimationFrame(() => {
      wrapper.style.height = `${wrapper.scrollHeight}px`;
    });
  }

  _close() {
    if (!this._isOpen) return;
    this._isOpen = false;

    const wrapper = this._contentWrapper;
    this._button.setAttribute("aria-expanded", "false");

    this._removeTransitionListener();
    wrapper.style.height = `${wrapper.scrollHeight}px`;
    wrapper.style.overflow = "hidden";

    window.requestAnimationFrame(() => {
      this._handleTransitionEnd = (e) => {
        if (e.target !== wrapper) return;
        this._removeTransitionListener();
        wrapper.style.height = null;
        wrapper.style.display = "none";
      };
      wrapper.addEventListener("transitionend", this._handleTransitionEnd);
      wrapper.style.height = "0";
    });
  }

  _removeTransitionListener() {
    if (this._handleTransitionEnd) {
      this._contentWrapper.removeEventListener(
        "transitionend",
        this._handleTransitionEnd,
      );
    }
  }

  _addToCart(button) {
    window.addToCart(
      { id: button.dataset.variantId, quantity: 1 },
      {
        onAfterAdd: () => {
          document.dispatchEvent(new CustomEvent("mini-cart:open"));
        },
        onError: (response) => {
          this._showError(
            response?.message || window.localeStrings.product.add_to_cart_error,
          );
        },
      },
    );
  }

  _showError(message) {
    const quickview = this.querySelector(".quickview");
    if (!quickview) return;

    const error = document.createElement("p");
    error.classList.add("quickview__error");
    error.innerHTML = message;
    quickview.append(error);

    setTimeout(() => error.remove(), 3000);
  }

  _getData() {
    return this._loader.getData(this.url, this.sectionId);
  }

  get url() {
    return this.getAttribute("url");
  }

  get sectionId() {
    return this.getAttribute("section-id") || "quickview";
  }

  get _content() {
    return this.querySelector("[data-content]");
  }
}

if (!customElements.get("quickview-trigger")) {
  customElements.define("quickview-trigger", QuickviewTrigger);
}
